import { Message } from '@shared/types';
import {
  Box,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, DEFAULT_PARAMETRIC_MODEL } from '@/lib/utils';
import { useConversation } from '@/services/conversationService';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useCallback, useMemo } from 'react';
import { TreeNode } from '@shared/Tree';
import {
  useRetryMessageMutation,
  useIsLoading,
  useSendContentMutation,
} from '@/services/messageService';
import { QuestionSuggestions } from '@/components/chat/QuestionSuggestions';
import { AnimatedEllipsis } from '@/components/chat/AnimatedEllipsis';
import { SpecDetails } from '@/components/spec/SpecDetails';
import { AgentTimeline } from '@/components/chat/AgentTimeline';
import { AskUserPrompt } from '@/components/chat/AskUserPrompt';
import { ContinuePrompt } from '@/components/chat/ContinuePrompt';
import { useTranslation } from 'react-i18next';
import { toPlainMessage } from '@/utils/plainMessage';

interface AssistantMessageProps {
  message: TreeNode<Message>;
}

export function AssistantMessage({ message }: AssistantMessageProps) {
  const { conversation, updateConversation } = useConversation();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const { mutate: retryMessage } = useRetryMessageMutation();
  const isLoading = useIsLoading();
  const { mutate: sendMessage } = useSendContentMutation({ conversation });
  const model = DEFAULT_PARAMETRIC_MODEL;
  const { t } = useTranslation('common');

  const changeLeaf = useCallback(
    (messageId: string) => {
      updateConversation?.({
        ...conversation,
        current_message_leaf_id: messageId,
      });
    },
    [updateConversation, conversation],
  );

  const branchIndex = useMemo(
    () => message.siblings.findIndex((branch) => branch.id === message.id),
    [message.siblings, message.id],
  );

  const leafNodes = useMemo(
    () =>
      message.siblings.map((branch) => {
        let current = branch;
        while (current.children && current.children.length > 0) {
          current = current.children[0];
        }
        return current;
      }),
    [message.siblings],
  );

  const hasVisibleContent =
    message.content.text ||
    (message.content.steps && message.content.steps.length > 0) ||
    message.content.artifact ||
    message.content.suggestions ||
    message.content.pendingQuestion ||
    message.content.pendingContinue ||
    message.content.error;

  return (
    <div className="flex w-full min-w-0 justify-start">
      <div className="w-full min-w-0 border-l-2 border-brand-teal pl-3 sm:max-w-[92%]">
        <div className="flex min-w-0 flex-col gap-3 overflow-hidden py-2 text-sm text-k-ink">
          {message.content.error ? (
            <div className="flex flex-col gap-1 px-1">
              <span className="font-bold text-brand-indigo">
                {t('chat.errorOccurred')}
              </span>
              <pre className="whitespace-pre-wrap break-all rounded-lg border-2 border-brand-indigo bg-brand-cream p-2 text-xs font-semibold text-brand-indigo">
                {message.content.error}
              </pre>
            </div>
          ) : hasVisibleContent ? (
            <>
              {message.content.steps && message.content.steps.length > 0 && (
                <AgentTimeline steps={message.content.steps} />
              )}
              {message.content.suggestions && (
                <QuestionSuggestions
                  suggestions={message.content.suggestions}
                  onSubmit={(answers) =>
                    sendMessage({
                      text: answers
                        .map(
                          ({ question, answer }) => `${question}: ${answer}`,
                        )
                        .join('\n'),
                      model,
                      thinking: true,
                    })
                  }
                />
              )}
              {message.content.pendingQuestion && (
                <AskUserPrompt
                  question={message.content.pendingQuestion}
                  disabled={isLoading}
                  onAnswer={(answer) =>
                    sendMessage({
                      text: answer,
                      model,
                      thinking: true,
                    })
                  }
                />
              )}
              {message.content.pendingContinue && !isLoading && (
                <ContinuePrompt
                  pendingContinue={message.content.pendingContinue}
                  disabled={isLoading}
                  onContinue={({ instruction, turbo }) =>
                    sendMessage({
                      text: instruction ?? t('chat.continueDefault'),
                      model,
                      thinking: true,
                      turboMode: turbo,
                    })
                  }
                  onDone={() => {
                    /* No-op: the user accepts the current state. The
                       pendingContinue UI unmounts when the user types a new
                       unrelated message, or when the message is re-rendered
                       without the pendingContinue field. */
                  }}
                />
              )}
              {message.content.artifact &&
                (message.content.artifact.spec?.parts.length ?? 0) > 0 && (
                  <ObjectButton
                    message={message}
                    currentMessage={currentMessage}
                    setCurrentMessage={setCurrentMessage}
                  />
                )}
              {message.content.artifact?.spec &&
                (message.content.artifact.spec.parts.length > 0 ||
                  message.content.artifact.spec.joints.length > 0 ||
                  message.content.artifact.spec.parameters.length > 0) && (
                  <SpecDetails message={message} />
                )}
            </>
          ) : (
            <AnimatedEllipsis color="synergy-neutral" size="sm" />
          )}

          {(message.siblings.length > 1 || message.parent_message_id) && (
            <div className="flex items-center gap-1">
              {message.parent_message_id && (
                <div className="flex items-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => {
                          retryMessage({ id: message.parent_message_id! });
                        }}
                        disabled={isLoading}
                        className="h-6 w-6 rounded-lg p-0"
                      >
                        <RefreshCw className="h-3 w-3 p-0 text-synergy-neutral-100" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>{t('chat.retry')}</span>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {message.siblings.length > 1 && (
                <div className="flex h-6 items-center gap-0.5 rounded-md bg-synergy-neutral-900">
                  <Button
                    disabled={branchIndex === 0 || isLoading}
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      changeLeaf(leafNodes[branchIndex - 1].id);
                    }}
                    className="h-full w-6 rounded-md rounded-r-none border-none p-0"
                  >
                    <ChevronLeft className="h-3 w-3 p-0 text-synergy-neutral-100" />
                  </Button>
                  <span className="text-xs tracking-widest text-synergy-neutral-100">
                    {branchIndex + 1}/{message.siblings.length}
                  </span>
                  <Button
                    disabled={
                      branchIndex === message.siblings.length - 1 || isLoading
                    }
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      changeLeaf(leafNodes[branchIndex + 1].id);
                    }}
                    className="h-full w-6 rounded-md rounded-l-none border-none p-0"
                  >
                    <ChevronRight className="h-3 w-3 p-0 text-synergy-neutral-100" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ObjectButton({
  message,
  currentMessage,
  setCurrentMessage,
}: {
  message: Message;
  currentMessage: Message | null;
  setCurrentMessage: (message: Message) => void;
}) {
  const { t } = useTranslation('common');
  let title = t('chat.defaultObjectTitle');
  if (message.content.artifact) {
    title = message.content.artifact.title;
  }

  const isActive = currentMessage && currentMessage.id === message.id;

  return (
    <Button
      variant="outline"
      className={cn(
        'group relative max-w-[280px] justify-start gap-2 rounded-xl border-2 px-3 py-2.5 text-xs font-bold transition-colors duration-150',
        isActive
          ? 'border-brand-indigo bg-brand-teal text-brand-indigo hover:bg-brand-teal-soft'
          : 'border-brand-indigo/15 bg-k-card text-brand-indigo hover:border-brand-indigo hover:bg-brand-teal-soft',
      )}
      onClick={() => setCurrentMessage(toPlainMessage(message))}
    >
      <Box
        className={cn(
          'h-4 w-4 flex-shrink-0',
          isActive ? 'text-brand-indigo' : 'text-brand-indigo/60',
        )}
      />
      <span className="truncate">{title}</span>
    </Button>
  );
}
